// The buyer's side of the payment stage — the Lovable "Buyer Payment rework" design
// (pages/Payment.tsx) on the real Klargo backend. Four moments, each with its own heading:
//   ready     — "Betala Anna med Swish": open Swish (a phone) or scan the code (a computer);
//   opened    — Swish has the payment; the buyer finishes there and comes back to say so;
//   reported  — the buyer has STATED they paid; the seller is the one who confirms;
//   confirmed — only ever the seller's confirmation.
// plus paying outside Klargo, and waiting for the seller's Swish number.
//
// Nothing here can move money or confirm it. Every state comes from the server; the Swish
// return only picks which explanation to show.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Landmark, Loader2 } from "lucide-react";
import { paymentApi, paymentErrorCode } from "./paymentApi";
import { formatPersonName } from "../../../lib/formatName";
import { buildSwishAppUrl, formatPaymentAmount, openSwishApp, swishReturnUrl } from "./swishApp2App";
import {
  ACTION_ROW, DealWaitingNotice, FactRows, H1_CLASS, LEAD_CLASS, META_CLASS, Notice, OUTLINE_ACTION, PRIMARY_ACTION, firstName,
} from "./PaymentParts";
import swishLogo from "../../../../assets/swish-logo.png";

// Swish is the buyer's way to pay only while the seller has enabled it (state.sellerSwishNumber).
// Until then the stage shows both ways side by side, Swish greyed out and saying why, and the
// other way - paying outside Klargo - stays available. The moment the seller enables Swish, the
// stage moves to the QR code / Swish button by itself.

// Where the stage goes next is the page's business: the page holds the router, and passing the
// move in keeps this panel renderable — and its destination assertable — on its own. The
// fallback is a real navigation, so a page that forgets still works rather than going nowhere.
const defaultAdvance = (to) => { window.location.assign(to); };

const openedKey = (contractId) => `klargo.swish.opened.${contractId}`;

// "Swish was opened for this instruction" survives a reload in this tab (sessionStorage), and is
// set again by a return from Swish in any tab or browser (the return URL names the instruction).
// Bound to the fingerprint, so a changed recipient never inherits it — and if the seller changed
// the number after Swish was opened, the buyer may already have paid the old one: they are told
// not to pay again and can report that payment (the server accepts numbers the seller confirmed).
function readOpened(contractId) {
  try { return sessionStorage.getItem(openedKey(contractId)); } catch (_) { return null; }
}
function writeOpened(contractId, fingerprint) {
  try {
    if (fingerprint) sessionStorage.setItem(openedKey(contractId), fingerprint);
    else sessionStorage.removeItem(openedKey(contractId));
  } catch (_) { /* storage unavailable */ }
}

const Busy = ({ label }) => (
  <>
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
    {label}
  </>
);

export default function BuyerPaymentPanel({ state, onState, onRefresh, t, language, reach, swishReturn, onAdvance = defaultAdvance, confirmedLive = false }) {
  const contractId = state.contractId;
  // Display only: legacy accounts may hold BankID-style CAPS names.
  const sellerName = formatPersonName(state.sellerName) || t("payment.sellerFallback");
  const vars = { seller: sellerName, sellerFirst: firstName(sellerName) || sellerName };

  // `chosen` is only what the buyer picked themselves. Until they pick, a deal whose seller has
  // enabled Swish opens on Swish (the QR code / Swish button).
  const [chosen, setChoice] = useState(state.buyerReportedMethod === "swish" ? "swish" : null);
  const swishEnabled = !!state.sellerSwishNumber;
  const choice = !swishEnabled ? "external" : chosen ?? "swish";
  const [instruction, setInstruction] = useState(null);
  const [instructionFailed, setInstructionFailed] = useState(null);
  const [qr, setQr] = useState({ status: "idle", uri: null, fingerprint: null });
  const [openedFor, setOpenedFor] = useState(() => readOpened(contractId));
  const [shownEarlier, setShownEarlier] = useState(null);
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState(null);
  const [notOpened, setNotOpened] = useState(false);
  const seenFingerprintRef = useRef(null);
  const returnHandledRef = useRef(false);
  const actedRef = useRef(false);
  const headingRef = useRef(null);

  const stage = state.stage;
  const fingerprint = state.instructionFingerprint;

  // ── PAYMENT HANDOFF (the reference's own rule) ──
  // The seller's confirmation arriving WHILE this stage waits for it is the deal moving on with
  // nothing left for the buyer to decide, so the stage hands over to Ägarbyte by itself. A
  // payment FOUND confirmed — a reload, a return, Back from Ägarbyte — is a different event and
  // keeps the manual Fortsätt, so a confirmed state alone can never bounce the buyer forward.
  // Which of the two happened is read off the server stages by DealPaymentStage, where they
  // change, and handed in as `confirmedLive`.
  const handoffStartedRef = useRef(false);

  useEffect(() => {
    if (!confirmedLive || handoffStartedRef.current) return;
    handoffStartedRef.current = true;
    onAdvance(`/Ownership/${contractId}`);
  }, [confirmedLive, contractId, onAdvance]);

  // ── The instruction the buyer pays against: refetched whenever the server's changes ──
  useEffect(() => {
    if (stage !== "readyToPay" || !fingerprint) return undefined;
    if (instruction?.fingerprint === fingerprint) return undefined;
    let alive = true;
    const changed = seenFingerprintRef.current && seenFingerprintRef.current !== fingerprint ? seenFingerprintRef.current : null;
    seenFingerprintRef.current = fingerprint;
    // The seller changed the recipient while the buyer had the old details — opened in Swish, or
    // shown as a QR code that may already have been scanned. Keep that instruction: the buyer
    // may have paid it, and must be able to say so instead of paying again.
    if (changed) setShownEarlier(changed);
    setInstructionFailed(null);
    paymentApi.getSwishInstruction(contractId)
      .then((next) => { if (alive) setInstruction(next); })
      .catch((err) => { if (alive) setInstructionFailed(paymentErrorCode(err) ?? "generic"); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, fingerprint, contractId]);

  // ── A return from Swish: an explanation, never a state change ──
  useEffect(() => {
    if (!swishReturn || returnHandledRef.current) return;
    returnHandledRef.current = true;
    if (stage !== "readyToPay" && stage !== "awaitingSwishNumber") return;   // already reported/confirmed
    setChoice("swish");
    // The instruction that was actually opened (named in the return URL, or remembered by this
    // tab) — not simply the current one, which the seller may have replaced while the buyer was
    // in Swish. Only a return that names nothing (not one Klargo built) falls back to the current.
    const openedNow = swishReturn.instructionFingerprint ?? readOpened(contractId) ?? fingerprint;
    if (openedNow) {
      writeOpened(contractId, openedNow);
      setOpenedFor(openedNow);
    }
    const key = ["paid", "notpaid", "unknown"].includes(swishReturn.result) ? swishReturn.result : "none";
    // A stale instruction has its own notice; only "not paid" still needs saying beside it.
    if (openedNow && fingerprint && openedNow !== fingerprint && key !== "notpaid") return;
    setNotice({
      tone: key === "notpaid" ? "red" : "slate",
      text: t(`payment.buyer.callback.${key}`, vars),
      testId: `payment-return-${key}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swishReturn, stage, fingerprint]);

  const opened = !!openedFor && openedFor === fingerprint;
  // An instruction the seller has since replaced, which the buyer may already have paid: one
  // opened in Swish (strongest), or one this page showed before the change.
  const openedEarlier = !!openedFor && !!fingerprint && openedFor !== fingerprint;
  const earlierFingerprint = stage !== "readyToPay" || !fingerprint ? null
    : openedEarlier ? openedFor
    : shownEarlier && shownEarlier !== fingerprint ? shownEarlier
    : null;
  const isReported = stage === "reported";
  const isConfirmed = stage === "confirmed";

  // The reference's moments, plus "unavailable": the seller has not enabled Swish and the buyer
  // has not (yet) chosen to pay outside Klargo.
  const view = isConfirmed ? "confirmed"
    : isReported ? "reported"
    : !swishEnabled && chosen !== "external" ? "unavailable"
    : choice === "external" ? "external"
    : opened && reach !== "qr" ? "opened"
    : "ready";
  const showsCode = view === "ready" && reach !== "app";
  /** Who the money goes to: the live instruction, then the deal's number. */
  const payeeNumber = instruction?.payeeDisplay ?? state.sellerSwishNumber;

  // ── The QR code, rendered by Swish on the server from the same instruction ──
  const loadQr = useCallback(() => {
    if (!instruction) return;
    // Always keyed by the instruction that was asked for, so one bad answer is one failure
    // (with a retry button), never a request loop.
    const requested = instruction.fingerprint;
    setQr({ status: "loading", uri: null, fingerprint: requested });
    paymentApi.getSwishQr(contractId)
      .then((res) => setQr(res?.fingerprint === requested && res?.imageDataUri
        ? { status: "ready", uri: res.imageDataUri, fingerprint: requested }
        // A code for any other instruction (e.g. the recipient just changed) is never shown.
        : { status: "failed", uri: null, fingerprint: requested }))
      .catch(() => setQr({ status: "failed", uri: null, fingerprint: requested }));
  }, [contractId, instruction]);

  useEffect(() => {
    if (!showsCode || !instruction) return;
    if (qr.fingerprint === instruction.fingerprint && qr.status !== "idle") return;
    loadQr();
  }, [showsCode, instruction, qr.fingerprint, qr.status, loadQr]);

  // What the buyer's own action caused takes focus, so it is read out.
  useEffect(() => {
    if (actedRef.current) headingRef.current?.focus({ preventScroll: true });
  }, [view]);

  // ── Actions ──
  const handleOpenSwish = () => {
    if (!instruction) return;
    actedRef.current = true;
    setNotOpened(false);
    setNotice(null);
    setShownEarlier(null);   // paying the new details now
    writeOpened(contractId, instruction.fingerprint);
    setOpenedFor(instruction.fingerprint);
    const returnUrl = swishReturnUrl(contractId, { fingerprint: instruction.fingerprint });
    openSwishApp(buildSwishAppUrl(instruction.payloadJson, returnUrl), {
      onNotOpened: () => setNotOpened(true),
    });
  };

  // `paidFingerprint`: report a Swish payment made against an earlier instruction (see openedEarlier).
  const handleReport = async (paidFingerprint = null) => {
    actedRef.current = true;
    setPending(paidFingerprint ? "reportEarlier" : "report");
    try {
      const method = paidFingerprint ? "swish" : choice === "external" ? "other" : "swish";
      const fingerprintPaid = paidFingerprint ?? (method === "swish" ? instruction?.fingerprint : null);
      const next = await paymentApi.reportPaid(contractId, method, fingerprintPaid);
      writeOpened(contractId, null);
      setNotice(null);
      onState(next);
    } catch (err) {
      const code = paymentErrorCode(err);
      setNotice({ tone: "red", text: t(`payment.errors.${code}`, { ...vars, defaultValue: t("payment.errors.generic") }), testId: "payment-error" });
      // The server no longer accepts that earlier instruction: stop offering it.
      if (paidFingerprint && code === "paymentInstructionChanged") {
        writeOpened(contractId, null);
        setOpenedFor(null);
        setShownEarlier(null);
      }
      if (code === "paymentInstructionChanged" || code === "paymentStateChanged" || code === "paymentAlreadyReported") onRefresh();
    } finally {
      setPending(null);
    }
  };

  const choose = (next) => {
    actedRef.current = true;
    setNotice(null);
    setChoice(next);
  };

  const HEADINGS = {
    ready: t("payment.buyer.heading", vars),
    unavailable: t("payment.buyer.heading", vars),
    opened: t("payment.buyer.openedTitle"),
    external: t("payment.buyer.externalTitle", vars),
    reported: t("payment.buyer.reportedTitle", vars),
    confirmed: t("payment.buyer.confirmedTitle"),
  };

  // The amount, who receives it and what Swish will show — the facts the buyer checks.
  const swishRows = (
    <div data-testid="payment-context-swish">
      <FactRows
        rows={[
          { key: "amount", label: t("payment.buyer.amount"), value: formatPaymentAmount(instruction?.amount ?? state.amount, language), testId: "payment-swish-amount" },
          { key: "recipient", label: t("payment.buyer.recipient"), value: sellerName, meta: payeeNumber, testId: "payment-recipient" },
          { key: "message", label: t("payment.buyer.message"), value: instruction?.message ?? state.swishMessage, nowrap: true, testId: "payment-swish-message" },
        ]}
      />
    </div>
  );

  const reportButton = (
    <button type="button" onClick={() => handleReport()} disabled={pending !== null} className={PRIMARY_ACTION} data-testid="payment-report">
      {pending === "report" ? <Busy label={t("payment.buyer.sending")} /> : t("payment.buyer.paid")}
    </button>
  );

  // One way of paying as a row: logo or icon, title, and what it is. The Swish row on its own
  // screen is informational (its code or button is already on screen); greyed out while the
  // seller has not enabled Swish; otherwise it is the way across to the other method.
  const optionRow = (to) => {
    const isSwish = to === "swish";
    const unavailable = isSwish && !swishEnabled;
    const active = isSwish && (view === "ready" || view === "opened");
    const title = t(isSwish ? "payment.buyer.paySwish" : "payment.buyer.payOutside");
    const description = isSwish ? (unavailable ? t("payment.buyer.swishUnavailable") : null) : t("payment.buyer.externalLead");
    const content = (
      <>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center" aria-hidden="true">
          {isSwish ? <img src={swishLogo} alt="" className="h-12 w-auto max-w-full object-contain" /> : <Landmark className="h-6 w-6 text-ds-muted" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] font-semibold leading-snug">{title}</span>
          {description && (
            <span className={`${META_CLASS} mt-1 block`} data-testid={isSwish ? "payment-swish-not-enabled" : undefined}>{description}</span>
          )}
        </span>
        {!unavailable && !active && <ArrowRight className="h-4 w-4 shrink-0 text-ds-muted" aria-hidden="true" />}
      </>
    );
    const rowClass = `flex min-h-24 w-full items-center gap-4 py-5 text-left ${unavailable ? "text-ds-muted" : "text-ds-ink"}`;
    if (active || unavailable) return <div className={rowClass} aria-disabled={unavailable || undefined} data-testid={isSwish ? "payment-choose-swish" : undefined}>{content}</div>;
    return (
      <button type="button" onClick={() => choose(to)} className={`${rowClass} bg-transparent hover:bg-ds-slate-bg`}
        data-testid={isSwish ? "payment-choose-swish" : "payment-choose-external"}>
        {content}
      </button>
    );
  };

  // The way across to the other method, under the moment's action.
  const switchMethod = (to) => <div className="mt-5 border-t border-ds-rule-row">{optionRow(to)}</div>;

  const instructionUnavailable = instructionFailed && view !== "external" && (
    <Notice tone="red" testId="payment-instruction-failed">
      {t(`payment.errors.${instructionFailed}`, { ...vars, defaultValue: t("payment.errors.generic") })}
    </Notice>
  );

  return (
    <>
    {/* One moment at a time: what the buyer's own action brings settles in (opacity only, as
        the reference does it) and takes focus; a cold load simply stands. */}
    <div key={view} className={actedRef.current ? "ds-doc-arrive" : undefined} data-testid={`payment-view-${view}`}>
      <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} focus:outline-none`}>{HEADINGS[view]}</h1>

      {view === "ready" && (
        <>
          <p className={LEAD_CLASS}>{t("payment.buyer.chooseIntro")}</p>
          <div className="mt-6 border-t border-ds-rule-row">{optionRow("swish")}</div>
          {showsCode ? (
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-7" data-testid="payment-qr-handoff">
              <div className="flex h-[180px] w-[180px] flex-none items-center justify-center">
                {qr.status === "ready" && qr.uri ? (
                  <img src={qr.uri} alt={t("payment.buyer.qrAlt", vars)} width={180} height={180}
                    className="block h-[180px] w-[180px]" data-testid="payment-qr" draggable={false} />
                ) : qr.status === "failed" ? (
                  <div className="text-center" data-testid="payment-qr-failed">
                    <p className={`${META_CLASS} m-0`}>{t("payment.buyer.qrFailed")}</p>
                    <button type="button" onClick={loadQr} className="ds-action-text mt-2" data-testid="payment-qr-retry">
                      {t("payment.buyer.qrRetry")}
                    </button>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-2 text-[13px] text-ds-muted" data-testid="payment-qr-loading">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{t("payment.buyer.qrLoading")}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1 sm:pt-4">
                <p className="m-0 text-[15px] font-semibold leading-[1.4] text-ds-ink">{t("payment.buyer.qrTitle")}</p>
                <p className={`${META_CLASS} mt-1`}>{t("payment.buyer.qrMeta")}</p>
                <div className="mt-4">{swishRows}</div>
              </div>
            </div>
          ) : (
            <div>{swishRows}</div>
          )}
          <p className={`${META_CLASS} mt-4`}>{t("payment.buyer.checkName", vars)}</p>
          {instructionUnavailable}
          <div className={ACTION_ROW}>
            {showsCode ? (
              <>
                {reportButton}
                {reach === "qr-and-app" && (
                  <button type="button" onClick={handleOpenSwish} disabled={!instruction || pending !== null}
                    className="ds-action-text sm:ml-3" data-testid="payment-open-swish">
                    {t("payment.buyer.openSwishHere")}
                  </button>
                )}
              </>
            ) : (
              <button type="button" onClick={handleOpenSwish} disabled={!instruction || pending !== null}
                className={PRIMARY_ACTION} data-testid="payment-open-swish">
                {instruction ? <>{t("payment.buyer.openSwish")}<ArrowRight className="h-4 w-4" aria-hidden="true" /></> : <Busy label={t("payment.buyer.preparing")} />}
              </button>
            )}
          </div>
          {!showsCode && <p className={`${META_CLASS} mt-2.5`}>{t("payment.buyer.installHint")}</p>}
          {switchMethod("external")}
        </>
      )}

      {view === "opened" && (
        <>
          <p className={LEAD_CLASS}>{t("payment.buyer.openedLead")}</p>
          <div className="mt-6 border-t border-ds-rule-row">{optionRow("swish")}</div>
          <div>{swishRows}</div>
          <p className={`${META_CLASS} mt-4`}>{t("payment.buyer.checkName", vars)}</p>
          {instructionUnavailable}
          <div className={ACTION_ROW}>
            {reportButton}
            <button type="button" onClick={handleOpenSwish} disabled={!instruction || pending !== null}
              className={OUTLINE_ACTION} data-testid="payment-reopen-swish">
              {t("payment.buyer.reopenSwish")}
            </button>
          </div>
          {switchMethod("external")}
        </>
      )}

      {view === "external" && (
        <div data-testid="payment-context-external">
          <p className={LEAD_CLASS}>{t("payment.buyer.externalLead")}</p>
          <p className={`${META_CLASS} mt-2`}>{t("payment.buyer.externalNote")}</p>
          <p className={`${META_CLASS} mt-4`}>{t("payment.buyer.reportingGuidance")}</p>
          <div className={ACTION_ROW}>{reportButton}</div>
          {/* Always shown, but usable only once the seller has enabled Swish: the stage then moves
              to the QR code / Swish button by itself. */}
          {switchMethod("swish")}
        </div>
      )}

      {view === "unavailable" && (
        <>
          <p className={LEAD_CLASS}>{t("payment.buyer.chooseIntro")}</p>
          <div className="mt-6 border-t border-ds-rule-row">
            <div className="border-b border-ds-rule-row">{optionRow("swish")}</div>
            {optionRow("external")}
          </div>
        </>
      )}

      {view === "reported" && (
        <>
          <p className={LEAD_CLASS}>{t("payment.buyer.reportedLead")}</p>
          <div className="mt-6 max-w-[58ch]" data-testid="payment-awaiting-seller">
            <DealWaitingNotice text={t("payment.buyer.waitingFor", vars)} t={t} />
          </div>
        </>
      )}

      {view === "confirmed" && (
        <div data-testid="payment-confirmed">
          <p className={LEAD_CLASS}>{t("payment.buyer.confirmedBody", vars)}</p>
          {/* A confirmed payment hands the deal to Ägarbyte — the seller's task, which the buyer
              waits out on its own stage. A confirmation that arrived while the buyer waited has
              already moved on by itself; this button is for a payment found confirmed. */}
          {!confirmedLive && (
            <div className="mt-7">
              <button type="button" onClick={() => onAdvance(`/Ownership/${contractId}`)}
                className={PRIMARY_ACTION} data-testid="payment-continue">
                {t("payment.buyer.continue")}
              </button>
            </div>
          )}
        </div>
      )}

      {earlierFingerprint && view === "ready" && (
        <Notice tone="red" testId="payment-number-changed">
          {t(openedEarlier ? "payment.buyer.numberChangedAfterOpen" : "payment.buyer.numberChanged", vars)}
          <span className="mt-2 block">
            <button type="button" onClick={() => handleReport(earlierFingerprint)} disabled={pending !== null}
              className="ds-action-text" data-testid="payment-report-earlier">
              {pending === "reportEarlier" ? <Busy label={t("payment.buyer.sending")} /> : t("payment.buyer.paidEarlier")}
            </button>
          </span>
        </Notice>
      )}
      {notOpened && (view === "ready" || view === "opened") && (
        <Notice testId="payment-not-opened">{t("payment.buyer.notOpenedHint")}</Notice>
      )}
      {notice && <Notice tone={notice.tone} testId={notice.testId}>{notice.text}</Notice>}
    </div>

    {/* Outside the moment, as the reference has it: quiet guidance that belongs to the stage rather
        than to whichever moment is on screen, so it does not re-mount every time the body changes. */}
    <div className={view === "reported" || view === "confirmed" ? "mt-8" : "mt-6"}>
      {view === "ready" && <p className={`${META_CLASS} m-0 mb-1.5`}>{t("payment.buyer.swishLead", vars)}</p>}
      {(view === "ready" || view === "opened") && <p className={`${META_CLASS} m-0 mb-1.5`}>{t("payment.buyer.limit")}</p>}
      {view !== "reported" && view !== "confirmed" && view !== "external" && <p className={`${META_CLASS} m-0`}>{t("payment.buyer.reportingGuidance")}</p>}
    </div>
    </>
  );
}
